// authenticationSlice.ts
import { createSelector, createSlice } from '@reduxjs/toolkit';
import endpoints from '../api/authenticationApi';
import { AuthenticationState } from '../types';
import { RootState } from '@app';

//~ Keep a reference to this slice's name for cleanup in case of persistReducer usage
export const name = 'authentication';

//~ Initialize your state here
const initialState: AuthenticationState = {
    loginTime: undefined,
    sessionExpiryTime: undefined,
    token: undefined,
    user: undefined,
};

export const authenticationSlice = createSlice({
    name,
    initialState,
    reducers: {
        //~ Define your synchronous reducers here
        clearAuthentication: () => {
            return initialState;
        },
        logout: () => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        //~ Add matchers for RTK Query endpoint lifecycles if this slice needs to react
        builder
            //~ Login related matchers
            .addMatcher(endpoints.login.matchFulfilled, (state, action) => {
                state.loginTime = action.payload.loginTime;
                state.sessionExpiryTime = action.payload.sessionExpiryTime;
                state.token = action.payload.token;
            })
            //~ Logout related matchers
            .addMatcher(endpoints.logout.matchFulfilled, () => {
                return initialState;
            })
            .addMatcher(
                endpoints.getUserData.matchFulfilled,
                (state, action) => {
                    state.user = action.payload;
                },
            );
    },
    selectors: {
        //~ Define your selectors here
        selectLoginTime: (sliceState) => sliceState.loginTime,
        selectSessionExpiryTime: (sliceState) => sliceState.sessionExpiryTime,
        selectToken: (sliceState) => sliceState.token,

        selectUserData: (sliceState) => sliceState.user,
        selectUserId: (sliceState) => sliceState.user?.id,
        selectUsername: (sliceState) => sliceState.user?.username,
        selectEmail: (sliceState) => sliceState.user?.email,
    },
});

//~ Move derived selectors here
const selectAuthSlice = (state: RootState) => state[name];

export const selectSessionExpiryTime = (state: RootState) =>
    selectAuthSlice(state).sessionExpiryTime;
export const selectUserData = (state: RootState) => selectAuthSlice(state).user;

//~ Derive Selectors requiring calculation here
export const selectIsAuthenticated = createSelector(
    [selectSessionExpiryTime, selectUserData],
    (sessionExpiryTime, user) => {
        return user && sessionExpiryTime
            ? Date.now() < sessionExpiryTime
            : false;
    },
);

export const selectRemainingSessionTime = createSelector(
    [selectSessionExpiryTime],
    (sessionExpiryTime) => {
        if (sessionExpiryTime) {
            return Math.max(0, sessionExpiryTime - Date.now());
        }
        return 0;
    },
);

//~ Export defined synchronous reducers here
export const { clearAuthentication, logout } = authenticationSlice.actions;

//~ Export defined selectors here
export const {
    selectLoginTime,
    selectEmail,
    selectUserId,
    selectUsername,
    selectToken,
} = authenticationSlice.selectors;

export const authenticationReducer = authenticationSlice.reducer;
export default authenticationSlice;
